/**
 * Approval workflow + new-customer wizard strings (`workflow.*`), merged through `extra.ts`.
 * `workflowEn` is typed against `workflowPl`, so a missing key is a compile error.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

const STEP_FIELDS_PL = {
  approvedCreditLimit: 'Zatwierdzony limit kupiecki',
  paymentTermDays: 'Termin płatności',
  conditions: 'Warunki',
  approvedPriceNotes: 'Zatwierdzone ceny i rabaty',
  deliveryDays: 'Dni dostaw',
  receivingHours: 'Godziny przyjęć',
  avizationRequired: 'Awizacja dostaw',
  avizationNote: 'Opis awizacji',
  restrictions: 'Ograniczenia',
  missingData: 'Braki danych',
  requirements: 'Wymagania obsługi',
  missingDataHint: 'Dodaj każdy brak osobno (Enter dodaje pozycję).',
  missingDataPlaceholder: 'Np. brak numeru konta',
  addItem: 'Dodaj pozycję',
  removeItem: 'Usuń: {item}',
  approvedPaymentMethod: 'Rodzaj płatności zaakceptowanej',
  b2bDecision: 'Konto na platformie B2B',
  routeId: 'Trasa dostaw',
};

export const workflowPl = {
  subtitle: 'Równoległe akceptacje działów: Handlowy, Logistyka i Zamówienia.',
  newCustomer: 'Nowy klient',
  tabs: { pending: 'Czekają na mnie', mine: 'Moje zgłoszenia', returned: 'Do poprawy', all: 'Wszystkie' },
  status: {
    PENDING: 'Oczekuje',
    IN_PROGRESS: 'W toku',
    RETURNED: 'Zwrócony do uzupełnienia',
    APPROVED: 'Zatwierdzony',
    REJECTED: 'Odrzucony',
    CANCELLED: 'Anulowany',
  },
  stepStatus: {
    PENDING: 'Czeka na decyzję',
    APPROVED: 'Zatwierdzono',
    RETURNED: 'Zwrócono',
    REJECTED: 'Odrzucono',
    SKIPPED: 'Pominięty',
  },
  list: {
    title: 'Wniosek',
    requester: 'Zgłaszający',
    currentStep: 'Akceptacje działów',
    status: 'Status',
    age: 'Wiek',
    step: 'Krok {current} z {total}',
    finished: 'Zakończony',
    waitingForMe: 'Czeka na Ciebie',
    waitingFor: 'Czeka na: {areas}',
    toFix: 'Do poprawy przez zgłaszającego',
    revision: 'rew. {n}',
    emptyReturnedTitle: 'Brak wniosków do poprawy',
    emptyReturnedBody: 'Gdy dział zwróci Twój wniosek do uzupełnienia, pojawi się tutaj.',
    emptyPendingTitle: 'Brak wniosków do akceptacji',
    emptyPendingBody: 'Gdy wniosek będzie czekał na decyzję Twojego działu, pojawi się tutaj.',
    emptyMineTitle: 'Nie zgłosiłeś jeszcze żadnego wniosku',
    emptyMineBody: 'Uruchom obieg akceptacji dla nowego klienta.',
    emptyAllTitle: 'Brak wniosków',
    emptyAllBody: 'Nie ma jeszcze żadnych wniosków w obiegu akceptacji.',
    loadError: 'Nie udało się pobrać wniosków.',
    requestedBy: 'zgłosił(a) {name}',
  },
  detail: {
    back: 'Akceptacje',
    notFound: 'Nie znaleziono wniosku lub brak dostępu.',
    customer: 'Klient',
    openCustomer: 'Karta klienta',
    lead: 'Lead',
    requester: 'Zgłaszający',
    createdAt: 'Zgłoszono',
    completedAt: 'Zakończono',
    payload: 'Dane wniosku',
    steps: 'Przebieg akceptacji',
    approvals: 'Akceptacje działów',
    return: 'Zwróć do uzupełnienia',
    returnedBanner: 'Zwrócono do uzupełnienia',
    returnedBodyMine: 'Popraw dane zgodnie z uwagami działów i wyślij wniosek ponownie. Do weryfikacji wrócą tylko te działy, których dotyczą zmiany.',
    returnedBody: 'Wniosek czeka na poprawki zgłaszającego.',
    resubmit: 'Popraw i wyślij ponownie',
    waitingForAreas: 'Czeka na: {areas}',
    waitingHint: 'Każdy dział decyduje niezależnie, w dowolnej kolejności.',
    current: 'Bieżący krok',
    dueAt: 'Termin: {date}',
    overdue: 'Po terminie: {date}',
    decidedBy: '{name} · {date}',
    waiting: 'Oczekuje na decyzję',
    waitingFor: 'Wymagane uprawnienie: {permission}',
    notStarted: 'Jeszcze nie rozpoczęty',
    approvedBanner: 'Wszystkie działy zatwierdziły wniosek',
    erpReady: 'Gotowe do eksportu do ERP',
    erpReadyAt: 'Dane przygotowane {date} — eksport do Subiekta w trybie półautomatycznym.',
    erpPending: 'Przygotowanie danych do eksportu do ERP…',
    rejectedBanner: 'Wniosek odrzucony',
    rejectedBody: 'Odrzucone przez dział: {step}.',
    cancelledBanner: 'Wniosek anulowany przez zgłaszającego',
    yourDecision: 'Czeka na Twoją decyzję',
    yourDecisionHint: 'Akceptacja „{step}” czeka na Twoją decyzję.',
    approve: 'Zatwierdź',
    reject: 'Odrzuć',
    cancel: 'Anuluj wniosek',
    cancelTitle: 'Anulować wniosek?',
    cancelBody: 'Wniosek zostanie zamknięty, a akceptujący nie będą musieli podejmować decyzji. Tej operacji nie można cofnąć.',
    cancelConfirm: 'Anuluj wniosek',
    cancelKeep: 'Zostaw wniosek',
    cancelled: 'Wniosek został anulowany.',
    actionError: 'Nie udało się wykonać operacji.',
  },
  decision: {
    approveTitle: 'Zatwierdzić: {step}?',
    approveBody: 'Twój dział zatwierdzi wniosek. Pozostałe działy decydują niezależnie.',
    approveBodyLast: 'To ostatnia brakująca akceptacja — po zatwierdzeniu klient zostanie aktywowany i będzie gotowy do eksportu do ERP.',
    returnTitle: 'Zwrócić do uzupełnienia: {step}?',
    returnBody: 'Wniosek wróci do zgłaszającego z Twoim komentarzem. Po poprawce wróci do weryfikacji.',
    rejectTitle: 'Odrzucić wniosek?',
    rejectBody: 'Odrzucenie kończy cały wniosek i jest ostateczne. Zgłaszający zobaczy Twój komentarz.',
    comment: 'Komentarz',
    commentOptional: 'Komentarz (opcjonalnie)',
    commentRequired: 'Przy zwrocie i odrzuceniu komentarz jest wymagany.',
    commentPlaceholder: 'Uzasadnij decyzję…',
    commentPlaceholderReturn: 'Napisz, co zgłaszający ma uzupełnić lub poprawić…',
    approvedToast: 'Akceptacja zapisana.',
    returnedToast: 'Wniosek zwrócony do uzupełnienia.',
    rejectedToast: 'Wniosek odrzucony.',
    confirmApprove: 'Zatwierdź',
    confirmReturn: 'Zwróć do uzupełnienia',
    confirmReject: 'Odrzuć wniosek',
    dataTitle: 'Zatwierdzone warunki działu',
    errors: {
      limit: 'Podaj kwotę od 0 do 1 000 000 000.',
      term: 'Podaj liczbę całkowitą dni (0–365).',
      tooLong: 'Tekst jest za długi (maks. 4000 znaków).',
      list: 'Maks. 20 pozycji, do 200 znaków każda.',
    },
    fields: STEP_FIELDS_PL,
  },
  stepData: {
    ...STEP_FIELDS_PL,
    yes: 'Tak',
    no: 'Nie',
    unset: 'Bez ustaleń',
    approvedTitle: 'Zatwierdzone warunki',
    conditionsTitle: 'Zatwierdzone warunki',
  },
  history: {
    title: 'Historia',
    empty: 'Brak zdarzeń.',
    emptyHint: 'Decyzje działów, zwroty i ponowne wysłania pojawią się tutaj.',
    events: '{count} zdarzeń',
    revision: 'Rewizja {n}',
    changedFields: 'Zmienione pola',
    backToReview: 'Do ponownej weryfikacji wróciły: {areas}.',
  },
  resubmit: {
    title: 'Popraw wniosek',
    subtitle: 'Uzupełnij dane zgodnie z uwagami działów i wyślij wniosek ponownie.',
    breadcrumb: 'Poprawa wniosku',
    returnedTitle: 'Uwagi działów',
    comment: 'Wiadomość do działów (opcjonalnie)',
    commentPlaceholder: 'Np. co zostało poprawione…',
    submit: 'Wyślij ponownie',
    reset: 'Przywróć dane wniosku',
    error: 'Nie udało się wysłać wniosku ponownie.',
    submitting: 'Wysyłanie…',
    submitted: 'Wniosek został wysłany ponownie.',
    notAllowedTitle: 'Tego wniosku nie można teraz poprawić',
    notAllowedBody: 'Wniosek można poprawić tylko po zwrocie do uzupełnienia i tylko jako zgłaszający.',
    toRequest: 'Wróć do wniosku',
    impactTitle: 'Co wróci do weryfikacji',
    impactReturned: 'Działy, które zwróciły wniosek, ocenią go ponownie: {areas}.',
    impactChanged: 'Zmiana pola „{field}” wraca też do: {areas}.',
    impactNone: 'Nie zmieniono jeszcze żadnego pola.',
    impactHint: 'Działy, które już zatwierdziły, a zmiany ich nie dotyczą, zachowują swoją akceptację.',
    unchangedWarn: 'Nie zmieniono żadnych danych — działy zobaczą ten sam wniosek.',
  },
  payload: {
    company: 'Dane firmy',
    contact: 'Kontakt',
    trade: 'Handel i ceny',
    logistics: 'Logistyka',
    payments: 'Płatności i limit',
    empty: 'Brak',
    days: '{count} dni',
    filled: '{done}/{total} pól',
  },
  wizard: {
    title: 'Nowy klient',
    subtitle: 'Karta nowego klienta — po wysłaniu wniosek trafi równolegle do trzech działów: Handlowy, Logistyka i Zamówienia.',
    breadcrumb: 'Nowy klient',
    stepOf: 'Krok {current} z {total}',
    next: 'Dalej',
    back: 'Wstecz',
    submit: 'Wyślij do akceptacji',
    submitting: 'Wysyłanie…',
    edit: 'Edytuj',
    savedLocally: 'Wersja robocza zapisana lokalnie',
    draftRestored: 'Przywrócono zapisaną wersję roboczą.',
    discardDraft: 'Wyczyść formularz',
    prefilled: 'Dane uzupełnione z leada „{name}”.',
    submitted: 'Wniosek został wysłany do akceptacji.',
    submitError: 'Nie udało się wysłać wniosku.',
    fixErrors: 'Popraw zaznaczone pola, aby kontynuować.',
    summaryTitle: 'Sprawdź dane przed wysłaniem',
    summaryHint: 'Po wysłaniu wniosek trafi do działów: Handlowy (ceny i limit kupiecki), Logistyka oraz Zamówienia — każdy decyduje niezależnie.',
    summaryErrors: 'Formularz zawiera błędy — popraw je przed wysłaniem.',
    flowTitle: 'Co się stanie dalej — działy akceptują równolegle',
    flow: { sales: 'Handlowy — ceny i limit kupiecki', logistics: 'Logistyka — dostawy i awizacja', orders: 'Zamówienia — dane i obsługa' },
    steps: {
      company: 'Dane firmy',
      contact: 'Kontakt',
      trade: 'Handel i ceny',
      logistics: 'Logistyka',
      payments: 'Płatności i limit',
      summary: 'Podsumowanie',
    },
    fields: {
      name: 'Nazwa firmy',
      nip: 'NIP',
      street: 'Ulica i numer',
      postalCode: 'Kod pocztowy',
      city: 'Miejscowość',
      contactName: 'Osoba kontaktowa',
      contactPhone: 'Telefon',
      contactEmail: 'E-mail',
      segment: 'Segment',
      customerClass: 'Klasa klienta',
      expectedMonthlyVolume: 'Oczekiwany obrót miesięczny (PLN)',
      priceNotes: 'Uzgodnione ceny i rabaty',
      receivingHours: 'Godziny przyjęć',
      deliveryDays: 'Dni dostaw',
      logisticsNotes: 'Uwagi logistyczne',
      creditLimitRequested: 'Wnioskowany limit kupiecki (PLN)',
      paymentTermDays: 'Termin płatności (dni)',
      latitude: 'Szerokość geograficzna (GPS)',
      longitude: 'Długość geograficzna (GPS)',
      shortName: 'Nazwa skrócona',
      deliveryStreet: 'Adres dostawy — ulica i numer',
      deliveryPostalCode: 'Adres dostawy — kod pocztowy',
      deliveryCity: 'Adres dostawy — miejscowość',
      paymentMethod: 'Forma płatności',
      targetDocument: 'Dokument docelowy',
      routeId: 'Trasa',
      deliveryPriority: 'Priorytet dostawy',
      avizationHours: 'Godziny awizacji',
      receiverPhone: 'Telefon osoby odbierającej',
      remarks: 'Uwagi',
      contacts: 'Osoby kontaktowe',
    },
    placeholders: {
      name: 'np. Restauracja Pod Lipami Sp. z o.o.',
      nip: '10 cyfr, bez kresek',
      street: 'ul. Polna 12/3',
      postalCode: '00-000',
      city: 'Warszawa',
      contactName: 'Imię i nazwisko',
      contactPhone: '+48 600 100 200',
      contactEmail: 'kontakt@firma.pl',
      segment: 'Wybierz segment',
      customerClass: 'Wybierz klasę',
      priceNotes: 'Np. cennik hurtowy −5% na warzywa korzeniowe, stała cena na sałaty przez 3 miesiące…',
      receivingHours: 'np. 5:00–9:00',
      logisticsNotes: 'Np. wjazd od podwórza, rampa nr 2, awizacja telefoniczna dzień wcześniej…',
    },
    hints: {
      nip: 'Sprawdzamy sumę kontrolną.',
      postalCode: 'Format 00-000.',
      expectedMonthlyVolume: 'Szacunek wartości zakupów netto miesięcznie.',
      priceNotes: 'Ten opis zobaczy osoba akceptująca ceny.',
      deliveryDays: 'Zaznacz dni, w których klient może przyjmować dostawy.',
      creditLimitRequested: '0 = płatność z góry / za pobraniem.',
    },
    errors: {
      required: 'To pole jest wymagane.',
      nip: 'Nieprawidłowy NIP (10 cyfr, suma kontrolna).',
      postalCode: 'Kod pocztowy w formacie 00-000.',
      email: 'Podaj poprawny adres e-mail.',
      phone: 'Podaj poprawny numer telefonu.',
      number: 'Podaj liczbę większą lub równą 0.',
      days: 'Wybierz co najmniej jeden dzień dostaw.',
      integer: 'Podaj liczbę całkowitą dni (0–365).',
    },
    segments: {
      HORECA: 'HoReCa',
      RETAIL: 'Handel detaliczny',
      CATERING: 'Catering',
      INSTITUTION: 'Instytucje',
      WHOLESALE: 'Hurt',
    },
    classes: { A: 'A — kluczowy', B: 'B — standard', C: 'C — drobny', STRATEGIC: 'Strategiczny' },
    weekdays: { MON: 'Pon', TUE: 'Wt', WED: 'Śr', THU: 'Czw', FRI: 'Pt', SAT: 'Sob', SUN: 'Nd' },
  },
};

const STEP_FIELDS_EN: typeof STEP_FIELDS_PL = {
  approvedCreditLimit: 'Approved credit limit',
  paymentTermDays: 'Payment term',
  conditions: 'Conditions',
  approvedPriceNotes: 'Approved prices and discounts',
  deliveryDays: 'Delivery days',
  receivingHours: 'Receiving hours',
  avizationRequired: 'Delivery notice',
  avizationNote: 'Notice details',
  restrictions: 'Restrictions',
  missingData: 'Missing data',
  requirements: 'Handling requirements',
  missingDataHint: 'Add each missing item separately (Enter adds an item).',
  missingDataPlaceholder: 'E.g. bank account number is missing',
  addItem: 'Add item',
  removeItem: 'Remove: {item}',
  approvedPaymentMethod: 'Approved payment method',
  b2bDecision: 'B2B platform account',
  routeId: 'Delivery route',
};

export const workflowEn: Shape<typeof workflowPl> = {
  subtitle: 'Parallel department approvals: Sales, Logistics and Orders.',
  newCustomer: 'New customer',
  tabs: { pending: 'Waiting for me', mine: 'My requests', returned: 'To fix', all: 'All' },
  status: {
    PENDING: 'Pending',
    IN_PROGRESS: 'In progress',
    RETURNED: 'Returned for completion',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
    CANCELLED: 'Cancelled',
  },
  stepStatus: {
    PENDING: 'Awaiting decision',
    APPROVED: 'Approved',
    RETURNED: 'Returned',
    REJECTED: 'Rejected',
    SKIPPED: 'Skipped',
  },
  list: {
    title: 'Request',
    requester: 'Requester',
    currentStep: 'Department approvals',
    status: 'Status',
    age: 'Age',
    step: 'Step {current} of {total}',
    finished: 'Finished',
    waitingForMe: 'Waiting for you',
    waitingFor: 'Waiting for: {areas}',
    toFix: 'To be fixed by the requester',
    revision: 'rev. {n}',
    emptyReturnedTitle: 'Nothing to fix',
    emptyReturnedBody: 'When a department returns your request for completion, it shows up here.',
    emptyPendingTitle: 'Nothing to approve',
    emptyPendingBody: 'When a request is waiting for your department’s decision, it shows up here.',
    emptyMineTitle: 'You have not submitted any requests yet',
    emptyMineBody: 'Start the approval workflow for a new customer.',
    emptyAllTitle: 'No requests',
    emptyAllBody: 'There are no requests in the approval workflow yet.',
    loadError: 'Could not load requests.',
    requestedBy: 'requested by {name}',
  },
  detail: {
    back: 'Approvals',
    notFound: 'Request not found or you do not have access to it.',
    customer: 'Customer',
    openCustomer: 'Customer file',
    lead: 'Lead',
    requester: 'Requester',
    createdAt: 'Submitted',
    completedAt: 'Completed',
    payload: 'Request data',
    steps: 'Approval progress',
    approvals: 'Department approvals',
    return: 'Return for completion',
    returnedBanner: 'Returned for completion',
    returnedBodyMine: 'Fix the data according to the departments’ comments and resubmit. Only the departments affected by your changes review it again.',
    returnedBody: 'The request is waiting for the requester’s corrections.',
    resubmit: 'Fix and resubmit',
    waitingForAreas: 'Waiting for: {areas}',
    waitingHint: 'Each department decides independently, in any order.',
    current: 'Current step',
    dueAt: 'Due: {date}',
    overdue: 'Overdue: {date}',
    decidedBy: '{name} · {date}',
    waiting: 'Waiting for a decision',
    waitingFor: 'Required permission: {permission}',
    notStarted: 'Not started yet',
    approvedBanner: 'All departments approved the request',
    erpReady: 'Ready for ERP export',
    erpReadyAt: 'Data prepared {date} — export to Subiekt in semi-automatic mode.',
    erpPending: 'Preparing data for the ERP export…',
    rejectedBanner: 'Request rejected',
    rejectedBody: 'Rejected by: {step}.',
    cancelledBanner: 'Request cancelled by the requester',
    yourDecision: 'Waiting for your decision',
    yourDecisionHint: 'The “{step}” approval is waiting for your decision.',
    approve: 'Approve',
    reject: 'Reject',
    cancel: 'Cancel request',
    cancelTitle: 'Cancel the request?',
    cancelBody: 'The request will be closed and approvers will no longer need to decide. This cannot be undone.',
    cancelConfirm: 'Cancel request',
    cancelKeep: 'Keep request',
    cancelled: 'The request was cancelled.',
    actionError: 'The operation failed.',
  },
  decision: {
    approveTitle: 'Approve: {step}?',
    approveBody: 'Your department approves the request. Other departments decide independently.',
    approveBodyLast: 'This is the last missing approval — once approved, the customer is activated and ready for the ERP export.',
    returnTitle: 'Return for completion: {step}?',
    returnBody: 'The request goes back to the requester with your comment. After the fix it returns for review.',
    rejectTitle: 'Reject the request?',
    rejectBody: 'Rejecting ends the whole request and is final. The requester will see your comment.',
    comment: 'Comment',
    commentOptional: 'Comment (optional)',
    commentRequired: 'A comment is required when returning or rejecting.',
    commentPlaceholder: 'Explain your decision…',
    commentPlaceholderReturn: 'Tell the requester what to complete or fix…',
    approvedToast: 'Approval saved.',
    returnedToast: 'Request returned for completion.',
    rejectedToast: 'Request rejected.',
    confirmApprove: 'Approve',
    confirmReturn: 'Return for completion',
    confirmReject: 'Reject request',
    dataTitle: 'Conditions approved by the department',
    errors: {
      limit: 'Enter an amount from 0 to 1,000,000,000.',
      term: 'Enter a whole number of days (0–365).',
      tooLong: 'The text is too long (max 4000 characters).',
      list: 'Max 20 items, up to 200 characters each.',
    },
    fields: STEP_FIELDS_EN,
  },
  stepData: {
    ...STEP_FIELDS_EN,
    yes: 'Yes',
    no: 'No',
    unset: 'Not agreed',
    approvedTitle: 'Approved conditions',
    conditionsTitle: 'Approved conditions',
  },
  history: {
    title: 'History',
    empty: 'No events.',
    emptyHint: 'Department decisions, returns and resubmissions will appear here.',
    events: '{count} events',
    revision: 'Revision {n}',
    changedFields: 'Changed fields',
    backToReview: 'Sent back for review: {areas}.',
  },
  resubmit: {
    title: 'Fix the request',
    subtitle: 'Complete the data according to the departments’ comments and resubmit.',
    breadcrumb: 'Fix request',
    returnedTitle: 'Department comments',
    comment: 'Message to the departments (optional)',
    commentPlaceholder: 'E.g. what was fixed…',
    submit: 'Resubmit',
    reset: 'Restore request data',
    error: 'Could not resubmit the request.',
    submitting: 'Sending…',
    submitted: 'The request was resubmitted.',
    notAllowedTitle: 'This request cannot be fixed now',
    notAllowedBody: 'A request can be fixed only after it was returned for completion, and only by the requester.',
    toRequest: 'Back to the request',
    impactTitle: 'What goes back for review',
    impactReturned: 'Departments that returned the request review it again: {areas}.',
    impactChanged: 'Changing “{field}” also sends it back to: {areas}.',
    impactNone: 'No field has been changed yet.',
    impactHint: 'Departments that already approved and are not affected by the changes keep their approval.',
    unchangedWarn: 'No data was changed — the departments will see the same request.',
  },
  payload: {
    company: 'Company details',
    contact: 'Contact',
    trade: 'Trade and prices',
    logistics: 'Logistics',
    payments: 'Payments and limit',
    empty: 'None',
    days: '{count} days',
    filled: '{done}/{total} fields',
  },
  wizard: {
    title: 'New customer',
    subtitle: 'New customer file — once submitted, the request goes in parallel to three departments: Sales, Logistics and Orders.',
    breadcrumb: 'New customer',
    stepOf: 'Step {current} of {total}',
    next: 'Next',
    back: 'Back',
    submit: 'Submit for approval',
    submitting: 'Submitting…',
    edit: 'Edit',
    savedLocally: 'Draft saved locally',
    draftRestored: 'Restored your saved draft.',
    discardDraft: 'Clear form',
    prefilled: 'Data prefilled from lead “{name}”.',
    submitted: 'The request was submitted for approval.',
    submitError: 'Could not submit the request.',
    fixErrors: 'Fix the highlighted fields to continue.',
    summaryTitle: 'Review the data before submitting',
    summaryHint: 'Once submitted, the request goes to Sales (prices and credit limit), Logistics and Orders — each decides independently.',
    summaryErrors: 'The form contains errors — fix them before submitting.',
    flowTitle: 'What happens next — departments approve in parallel',
    flow: { sales: 'Sales — prices and credit limit', logistics: 'Logistics — deliveries and notice', orders: 'Orders — data and handling' },
    steps: {
      company: 'Company',
      contact: 'Contact',
      trade: 'Trade and prices',
      logistics: 'Logistics',
      payments: 'Payments and limit',
      summary: 'Summary',
    },
    fields: {
      name: 'Company name',
      nip: 'Tax ID (NIP)',
      street: 'Street and number',
      postalCode: 'Postal code',
      city: 'City',
      contactName: 'Contact person',
      contactPhone: 'Phone',
      contactEmail: 'E-mail',
      segment: 'Segment',
      customerClass: 'Customer class',
      expectedMonthlyVolume: 'Expected monthly turnover (PLN)',
      priceNotes: 'Agreed prices and discounts',
      receivingHours: 'Receiving hours',
      deliveryDays: 'Delivery days',
      logisticsNotes: 'Logistics notes',
      creditLimitRequested: 'Requested credit limit (PLN)',
      paymentTermDays: 'Payment term (days)',
      latitude: 'Latitude (GPS)',
      longitude: 'Longitude (GPS)',
      shortName: 'Short name',
      deliveryStreet: 'Delivery address — street and number',
      deliveryPostalCode: 'Delivery address — postal code',
      deliveryCity: 'Delivery address — city',
      paymentMethod: 'Payment method',
      targetDocument: 'Target document',
      routeId: 'Route',
      deliveryPriority: 'Delivery priority',
      avizationHours: 'Delivery notice hours',
      receiverPhone: 'Delivery receiver phone',
      remarks: 'Remarks',
      contacts: 'Contact persons',
    },
    placeholders: {
      name: 'e.g. Pod Lipami Restaurant Ltd.',
      nip: '10 digits, no dashes',
      street: '12/3 Polna St.',
      postalCode: '00-000',
      city: 'Warsaw',
      contactName: 'First and last name',
      contactPhone: '+48 600 100 200',
      contactEmail: 'contact@company.com',
      segment: 'Select a segment',
      customerClass: 'Select a class',
      priceNotes: 'E.g. wholesale price list −5% on root vegetables, fixed lettuce price for 3 months…',
      receivingHours: 'e.g. 5:00–9:00',
      logisticsNotes: 'E.g. courtyard entrance, ramp no. 2, phone notice the day before…',
    },
    hints: {
      nip: 'We validate the checksum.',
      postalCode: 'Format 00-000.',
      expectedMonthlyVolume: 'Estimated net purchases per month.',
      priceNotes: 'The price approver will read this description.',
      deliveryDays: 'Select the days the customer can receive deliveries.',
      creditLimitRequested: '0 = prepayment / cash on delivery.',
    },
    errors: {
      required: 'This field is required.',
      nip: 'Invalid NIP (10 digits, checksum).',
      postalCode: 'Postal code in the 00-000 format.',
      email: 'Enter a valid e-mail address.',
      phone: 'Enter a valid phone number.',
      number: 'Enter a number greater than or equal to 0.',
      days: 'Select at least one delivery day.',
      integer: 'Enter a whole number of days (0–365).',
    },
    segments: {
      HORECA: 'HoReCa',
      RETAIL: 'Retail',
      CATERING: 'Catering',
      INSTITUTION: 'Institutions',
      WHOLESALE: 'Wholesale',
    },
    classes: { A: 'A — key account', B: 'B — standard', C: 'C — small', STRATEGIC: 'Strategic' },
    weekdays: { MON: 'Mon', TUE: 'Tue', WED: 'Wed', THU: 'Thu', FRI: 'Fri', SAT: 'Sat', SUN: 'Sun' },
  },
};
